import React from 'react'
import {useTranslation} from 'react-i18next'
import {RouteOptimization, TransportType} from 'app/utils/common/constants'
import './style.scss'

// Maps a category's dispatchMode to its Transportation Method; null when B8ak sent no known value
export const getTransportType = (dispatchMode?: string | null): string | null => {
  switch (dispatchMode) {
    case RouteOptimization.SelfDrivenTransport:
      return TransportType.SelfDriven
    case RouteOptimization.NoRouteOptimization:
    case RouteOptimization.WithRouteOptimization:
      return TransportType.DesignateDriver
    default:
      return null
  }
}

interface ReadOnlyRadioGroupProps {
  name: string
  legend: string
  options: {value: string; label: string}[]
  value: string | null
}

// A disabled fieldset: the options are visible, but none of them can be picked
const ReadOnlyRadioGroup: React.FC<ReadOnlyRadioGroupProps> = ({name, legend, options, value}) => (
  <fieldset className='transportation-config__group' disabled>
    <legend className='transportation-config__legend'>
      {legend}
      <span className='transportation-config__required'>*</span>
    </legend>
    {options.map((option) => (
      <label className='transportation-config__option' key={option.value}>
        <input
          type='radio'
          className='transportation-config__radio'
          name={name}
          value={option.value}
          checked={option.value === value}
          readOnly
        />
        <span className='transportation-config__option-label'>{option.label}</span>
      </label>
    ))}
  </fieldset>
)

interface TransportationConfigProps {
  dispatchMode?: string | null
}

// CNT-191: B8ak is the only source of truth for these values, so this view never offers a control to change them
const TransportationConfig: React.FC<TransportationConfigProps> = ({dispatchMode}) => {
  const {t} = useTranslation()

  const transportType = getTransportType(dispatchMode)

  return (
    <div className='transportation-config'>
      <ReadOnlyRadioGroup
        name='transportation-method'
        legend={t('SERVICE_MANAGEMENT_PAGE.TRANSPORTATION_CONFIG.TRANSPORTATION_QUESTION')}
        value={transportType}
        options={[
          {
            value: TransportType.SelfDriven,
            label: t('SERVICE_MANAGEMENT_PAGE.TRANSPORTATION_CONFIG.SELF_DRIVEN_TEAM'),
          },
          {
            value: TransportType.DesignateDriver,
            label: t('SERVICE_MANAGEMENT_PAGE.TRANSPORTATION_CONFIG.DESIGNATED_DRIVER'),
          },
        ]}
      />

      {!transportType && (
        <p className='transportation-config__empty'>
          {t('SERVICE_MANAGEMENT_PAGE.TRANSPORTATION_CONFIG.NOT_CONFIGURED')}
        </p>
      )}

      {/* Routing Mode only applies to a Designated Driver - Self-Driven Teams never render it */}
      {transportType === TransportType.DesignateDriver && (
        <ReadOnlyRadioGroup
          name='routing-mode'
          legend={t('SERVICE_MANAGEMENT_PAGE.TRANSPORTATION_CONFIG.ROUTING_MODE')}
          value={
            dispatchMode === RouteOptimization.WithRouteOptimization
              ? RouteOptimization.WithRouteOptimization
              : RouteOptimization.NoRouteOptimization
          }
          options={[
            {
              value: RouteOptimization.NoRouteOptimization,
              label: t('SERVICE_MANAGEMENT_PAGE.TRANSPORTATION_CONFIG.WITHOUT_ROUTE_OPTIMIZATION'),
            },
            {
              value: RouteOptimization.WithRouteOptimization,
              label: t('SERVICE_MANAGEMENT_PAGE.TRANSPORTATION_CONFIG.WITH_ROUTE_OPTIMIZATION'),
            },
          ]}
        />
      )}
    </div>
  )
}

export default TransportationConfig
